import { AccountType, Store, StoreCSVInfo } from '../../../types';
import { useState, useEffect, useCallback } from "react";
import { Divider } from '@mui/material';
import { createLog, editStoreCSVInfo, getCSVAccountData } from '../../../lib/admin';
import { StoreAccountDropdown } from './StoreAccountDropdown';
import StickySaveBar from '../../global/StickySaveBar';
import Notification from '../../global/Notification';
import { useHasScope } from '../../../utils/Permission';
import csvIcon from "../../../assets/management/csv.svg";

type Props =
    {
        store: Store,
        hasUnsavedChanges: boolean,
        setHasUnsavedChanges: (flag: boolean) => void,
        hasBeenUpdated: boolean,
        setHasBeenUpdated: (flag: boolean) => void
    }

type CSVValues = {
    accountId: number | string,
    csv: string,
}

const valuesFromStore = (store: Store): CSVValues => ({
    accountId: store.csvAccountId != undefined ? store.csvAccountId : "Not available",
    csv: store.csv != undefined ? store.csv : "Not available",
});

export default function StoreCSVForm({ store, setHasUnsavedChanges, setHasBeenUpdated }: Props) {
    const [baseline, setBaseline] = useState<CSVValues>(() => valuesFromStore(store));
    const [drafts, setDrafts] = useState<CSVValues>(baseline);
    const [accountOptions, setAccountOptions] = useState<AccountType[]>([]);
    const [dropdownKey, setDropdownKey] = useState(0);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const hasUpdateCSVInfo = useHasScope('update:csvconfiguration');

    const dismissSaveError = useCallback(() => setSaveError(null), []);
    const closeSuccessMessage = useCallback(() => setSuccessMessage(null), []);

    const hasChanges = drafts.accountId !== baseline.accountId || drafts.csv !== baseline.csv;

    useEffect(() => {
        setHasUnsavedChanges(hasChanges);
    }, [hasChanges, setHasUnsavedChanges]);

    const onDeletedAccount = () => {
        setDrafts((current) => ({ ...current, accountId: "none" }));
    }

    const onSelectedAccount = (id: number) => {
        if (accountOptions) {
            const accountSelected = accountOptions.find(account => account.id === id);
            if (accountSelected) {
                setDrafts((current) => ({ ...current, accountId: id }));
            }
        }
    }

    const handleSave = async () => {
        if (!hasUpdateCSVInfo) {
            setSaveError("You do not have permission to update this store.");
            return;
        }
        const { accountId, csv } = drafts;
        const editedStore: StoreCSVInfo = {
            id: store.id,
            csv: store.csv,
            csvAccountId: store.csvAccountId
        }
        if (accountId == "none")
            editedStore.csvAccountId = null;
        if (accountId !== store.csvAccountId && accountId !== "Not available" && accountId !== "") {
            const parsedAccountId = typeof accountId === "string" ? parseInt(accountId) : accountId;
            if (!isNaN(parsedAccountId)) editedStore.csvAccountId = parsedAccountId;
        }
        if (csv.trim() === "" || csv == 'Not available') {
            setSaveError("Invalid values. Try again.");
            return;
        }
        editedStore.csv = csv;

        setSaveError(null);
        setSaving(true);
        try {
            const response = await editStoreCSVInfo(editedStore);
            if (response) {
                createLog({
                    storeId: store.id,
                    entityType: 'storeSettings',
                    section: 'csv',
                    action: 'save',
                    lastValue: baseline,
                    newValue: drafts,
                });
                setBaseline(drafts);
                setSuccessMessage("Changes saved successfully.");
                setHasBeenUpdated(true);
            } else {
                setSaveError("An error occurred. Please try again later.");
            }
        } catch (error) {
            console.error("Error saving CSV configuration:", error);
            setSaveError("An error occurred. Please try again later.");
        } finally {
            setSaving(false);
        }
    };

    const handleCancelChanges = () => {
        setDrafts(baseline);
        setDropdownKey((key) => key + 1);
        setSaveError(null);
    };

    useEffect(() => {
        updateDropdown();
    }, [])

    const updateDropdown = async () => {
        const response = await getCSVAccountData();
        setAccountOptions(response);
    }

    return (
        <div className="sync-management">
            <div className="sync-header">
                <p>Shopify Stores/{store.friendlyName}</p>
                <h1><img src={csvIcon} alt="" />CSV Configuration</h1>
            </div>
            <Divider className="divider" />
            <div className="form-container">
                <div className="column">
                    <div className="field">
                        <p className="field-label">This kiosk associated account:</p>

                        <StoreAccountDropdown
                            key={dropdownKey}
                            defaultValue={typeof baseline.accountId === "number" ? baseline.accountId : null}
                            accountOptions={accountOptions}
                            onSelectAccount={onSelectedAccount}
                            onDeletedAccount={onDeletedAccount}
                            disabled={saving}
                        />

                    </div>
                    <div className="field">
                        <p className="field-label">CSV Name</p>
                        <div className="field-edit">
                            <input
                                type="text"
                                value={drafts.csv}
                                disabled={saving}
                                onChange={(e) => {
                                    const { value } = e.target;
                                    setDrafts((current) => ({ ...current, csv: value }));
                                }} />
                        </div>
                    </div>
                </div>
            </div>
            <StickySaveBar
                visible={hasChanges}
                saving={saving}
                onSave={handleSave}
                onCancel={handleCancelChanges}
                alignWithSelector=".leftVertical"
                errorMessage={saveError ?? undefined}
                onErrorDismiss={dismissSaveError}
            />
            <Notification message={successMessage} type="success" onClose={closeSuccessMessage} />
        </div>
    );
}